import { useState } from "react";
import { toast } from "sonner";
import { EmailShareDialog } from "@/components/EmailShareDialog";
import {
  XLogo, FacebookLogo, WhatsappLogo, TelegramLogo, LinkedinLogo,
  EnvelopeSimple, Link as LinkIcon, ShareNetwork,
} from "@phosphor-icons/react";

/**
 * ShareButtons — cross-platform share row for public pages.
 *
 * Channels: X (Twitter), Facebook, WhatsApp, Telegram, LinkedIn, Email, Copy link.
 *
 * Email button behaviour (2026-02-26 fix):
 *   Previously the Email button was an <a href="mailto:..." target="_blank">.
 *   On Chrome (and Edge) when a user has ever picked a webmail provider like
 *   IONOS Mail, Gmail or Yahoo as their mailto handler, `target="_blank"`
 *   causes the new tab to redirect to that provider's login page instead of
 *   opening the OS mail client. Now:
 *     • On mobile / touch devices (or anywhere navigator.share exists) we
 *       open the native OS share sheet — the user picks Mail, Gmail,
 *       Outlook, WhatsApp, whatever they actually use.
 *     • On desktop we set window.location.href = mailto:... in the SAME tab
 *       (no target="_blank"). Chrome/Edge/Firefox all honour the default
 *       mailto handler correctly this way.
 *   Result: no more accidental IONOS/Gmail webmail hijack.
 *
 * Props
 *   url        (required) — canonical URL to share (falls back to window.location.href)
 *   title      (required) — page/article title, used as share text
 *   summary    (optional) — extra descriptive text; where the channel accepts it
 *                            (X/Telegram/Email) we append after the title.
 *   ogPath     (optional) — backend path like `/api/share/recipe/xyz` that
 *                            returns server-rendered Open Graph HTML for
 *                            crawlers that don't run JS (Facebook, WhatsApp,
 *                            LinkedIn, Telegram, iMessage). If provided, we
 *                            share this URL to those channels so they get a
 *                            proper rich preview card. "Copy link" always
 *                            gives the clean canonical URL for humans.
 *   variant    "row" (default) | "compact" — compact hides labels, icons only.
 *   dataTestidPrefix — override for the test ids so multiple share bars on
 *                       one page don't clash (default: "share").
 */
export default function ShareButtons({
  url,
  title,
  summary = "",
  ogPath = "",
  variant = "row",
  dataTestidPrefix = "share",
}) {
  const [copied, setCopied] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const canonicalUrl = url || (typeof window !== "undefined" ? window.location.href : "");
  const shareTitle = title || "Recipe Filer";
  // When the caller supplies an OG-friendly backend path, we build an
  // absolute URL out of the current origin so crawlers see the full URL.
  // If ogPath is empty we fall back to the canonical URL — same as before.
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const ogUrl = ogPath ? `${origin}${ogPath}` : canonicalUrl;

  const encodedOgUrl = encodeURIComponent(ogUrl);
  const encodedTitle = encodeURIComponent(shareTitle);
  // Precombined helpers — X/WhatsApp want URL appended to the text so we
  // build the composite string once.
  const textPlusOgUrl = encodeURIComponent(`${shareTitle}${summary ? " — " + summary : ""} ${ogUrl}`.trim());

  const channels = [
    {
      key: "x",
      label: "X",
      icon: XLogo,
      // X does execute limited JS but their card fetcher is unreliable for
      // SPAs — send the OG URL so they can crawl the meta tags directly.
      href: `https://twitter.com/intent/tweet?text=${encodedTitle}&url=${encodedOgUrl}`,
      brand: "hover:bg-black hover:text-white",
    },
    {
      key: "facebook",
      label: "Facebook",
      icon: FacebookLogo,
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodedOgUrl}`,
      brand: "hover:bg-[#1877F2] hover:text-white",
    },
    {
      key: "whatsapp",
      label: "WhatsApp",
      icon: WhatsappLogo,
      href: `https://api.whatsapp.com/send?text=${textPlusOgUrl}`,
      brand: "hover:bg-[#25D366] hover:text-white",
    },
    {
      key: "telegram",
      label: "Telegram",
      icon: TelegramLogo,
      href: `https://t.me/share/url?url=${encodedOgUrl}&text=${encodedTitle}`,
      brand: "hover:bg-[#26A5E4] hover:text-white",
    },
    {
      key: "linkedin",
      label: "LinkedIn",
      icon: LinkedinLogo,
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedOgUrl}`,
      brand: "hover:bg-[#0A66C2] hover:text-white",
    },
  ];

  const copyLink = async () => {
    // Humans copy-pasting URLs want the clean canonical, not the /api/share
    // preview URL. Social crawlers use the intents above which already point
    // at ogUrl.
    try {
      await navigator.clipboard.writeText(canonicalUrl);
      setCopied(true);
      toast.success("Link copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Legacy fallback for browsers that block clipboard.writeText over
      // insecure contexts — spawn a hidden textarea and use execCommand.
      const ta = document.createElement("textarea");
      ta.value = canonicalUrl;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand("copy");
        setCopied(true);
        toast.success("Link copied");
        setTimeout(() => setCopied(false), 2000);
      } catch {
        toast.error("Couldn't copy link. Try selecting it manually.");
      }
      document.body.removeChild(ta);
    }
  };

  const isCompact = variant === "compact";
  // Phones get icon-only 40px buttons on one row; labels return from sm up.
  const btnBase = `inline-flex items-center justify-center gap-1.5 rounded-full border border-rf-ink/10 bg-white w-10 h-10 sm:w-auto sm:h-auto sm:px-3 sm:py-1.5 text-xs font-bold text-rf-ink transition-colors ${isCompact ? "sm:!px-2 sm:!py-2" : ""}`;

  return (
    <div
      data-testid={`${dataTestidPrefix}-buttons`}
      className="inline-flex flex-wrap items-center gap-1.5 sm:gap-2"
    >
      {!isCompact && (
        <span className="hidden sm:inline-flex text-xs uppercase tracking-wider text-rf-ink-soft font-bold mr-1 items-center gap-1.5">
          <ShareNetwork size={14} weight="duotone" /> Share
        </span>
      )}
      {channels.map((c) => {
        const Icon = c.icon;
        return (
          <a
            key={c.key}
            href={c.href}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`${dataTestidPrefix}-${c.key}`}
            aria-label={`Share on ${c.label}`}
            className={`${btnBase} ${c.brand}`}
          >
            <Icon size={isCompact ? 18 : 14} weight="fill" className="max-sm:w-[18px] max-sm:h-[18px]" />
            {!isCompact && <span className="hidden sm:inline">{c.label}</span>}
          </a>
        );
      })}
      {/* Email — native share on mobile, plain same-tab mailto: on desktop.
          Rendered as an <a href="mailto:..."> so middle-click / right-click →
          Copy Link Address / long-press still work like a normal link, but
          we intercept the click to prefer navigator.share. */}
      <button
        type="button"
        onClick={() => setEmailOpen(true)}
        data-testid={`${dataTestidPrefix}-email`}
        aria-label="Share by email"
        className={`${btnBase} hover:bg-rf-orange hover:text-white`}
      >
        <EnvelopeSimple size={isCompact ? 18 : 14} weight="fill" className="max-sm:w-[18px] max-sm:h-[18px]" />
        {!isCompact && <span className="hidden sm:inline">Email</span>}
      </button>
      <EmailShareDialog open={emailOpen} onOpenChange={setEmailOpen} title={shareTitle} summary={summary} url={canonicalUrl} kind={dataTestidPrefix} />
      <button
        type="button"
        onClick={copyLink}
        data-testid={`${dataTestidPrefix}-copy`}
        aria-label="Copy link"
        className={`${btnBase} hover:bg-rf-ink hover:text-white`}
      >
        <LinkIcon size={isCompact ? 18 : 14} weight="fill" className="max-sm:w-[18px] max-sm:h-[18px]" />
        {!isCompact && <span className="hidden sm:inline">{copied ? "Copied!" : "Copy link"}</span>}
      </button>
    </div>
  );
}
